import { reactive } from "vue";
import { registeredComponents } from "../init-builder.js";
import {
    fetchEntries,
    fetchOneEntry,
    Content,
    isPreviewing,
    isEditing,
    getBuilderSearchParams,
} from "@builder.io/sdk-vue";
import EventBus from "../event-bus";
import { trackStartedCustomizer } from "../utils/builderTracking";
import { getBuilderMergedSearchParams } from "../utils/builderPreview";
import {
    captureCustomizerException,
    captureCustomizerMessage,
    incrementCustomizerMetric,
} from "../utils/sentry";
import { safeGetItem, safeRemoveItem, safeSetItem } from "../utils/safeStorage";
import {
    getNumericVariantId,
    resolveCanonicalBuilderRoute,
} from "../utils/builderCanonicalRoute";

const STEP_STORAGE_KEY_MAP = {
    StepOne: "step1Storagekey",
    StepTwo: "step2Storagekey",
    StepThree: "step3Storagekey",
};

const STEP_DEFAULT_STORAGE_KEYS = {
    StepOne: "step1Values",
    StepTwo: "step2Values",
    StepThree: "step3Values",
};

const DEV_FALLBACK_SETTINGS = {
    step1Url: "/webhook/step1",
    step2Url: "/webhook/step2",
    step3Url: "/webhook/step3",
    variant: "default",
    product: "test",
};

const isDevMode = process.env.VUE_APP_DEV_MODE === "true";
const BUILDER_STEP_REDIRECT_KEY_PREFIX = "customizer:builderStepRedirect";
const BUILDER_CONTENT_RELOAD_KEY_PREFIX = "customizer:builderContentReload";
const BUILDER_CONTENT_RETRY_DELAY_MS = 600;
const BUILDER_CONTENT_RELOAD_WINDOW_MS = 5 * 60 * 1000;
const BUILDER_CANONICAL_ROUTE_LIMIT = 100;
const LEGACY_EDITOR_SETTINGS_JS_CODE_PATTERN =
    /Builder\.register\(["']editor\.settings["'][\s\S]*designTokens/;

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const buildPageSettingsFromContent = (stepName, contentData = {}) => {
    const storageKey =
        contentData[STEP_STORAGE_KEY_MAP[stepName]] ||
        STEP_DEFAULT_STORAGE_KEYS[stepName] ||
        "step1Values";

    return {
        step1Url: contentData.step1Url || "",
        step2Url: contentData.step2Url || "",
        step3Url: contentData.step3Url || "",
        storageKey,
        step1StorageKey: contentData.step1Storagekey || STEP_DEFAULT_STORAGE_KEYS.StepOne,
        step2StorageKey: contentData.step2Storagekey || STEP_DEFAULT_STORAGE_KEYS.StepTwo,
        step3StorageKey: contentData.step3Storagekey || STEP_DEFAULT_STORAGE_KEYS.StepThree,
        variant: contentData.variant || "",
        product: contentData.product || "",
    };
};

const buildDevFallbackSettings = (stepName) => {
    const storageKey =
        STEP_DEFAULT_STORAGE_KEYS[stepName] || STEP_DEFAULT_STORAGE_KEYS.StepOne;

    return {
        ...DEV_FALLBACK_SETTINGS,
        storageKey,
        step1StorageKey: STEP_DEFAULT_STORAGE_KEYS.StepOne,
        step2StorageKey: STEP_DEFAULT_STORAGE_KEYS.StepTwo,
        step3StorageKey: STEP_DEFAULT_STORAGE_KEYS.StepThree,
    };
};

const sanitizeBuilderContent = (content) => {
    const jsCode = content?.data?.jsCode;

    if (
        typeof jsCode !== "string" ||
        !LEGACY_EDITOR_SETTINGS_JS_CODE_PATTERN.test(jsCode)
    ) {
        return content;
    }

    return {
        ...content,
        data: {
            ...content.data,
            jsCode: "",
        },
    };
};

export default {
    components: {
        Content,
    },
    
    data() {
        return {
            canShowContent: false,
            builderContentMissing: false,
            builderCanonicalRouteStatus: "not_checked",
            content: null,
            model: "page",
            apiKey: "5c32841cdfc646708f6dc9b494dadddc",
            pageSettings: reactive({
                step1Url: "",
                step2Url: "",
                step3Url: "",
                storageKey: "",
                step1StorageKey: "",
                step2StorageKey: "",
                step3StorageKey: "",
                variant: "",
                product: "",
            }),
        };
    },
    
    provide() {
        return {
            pageSettings: this.pageSettings,
        };
    },
    
    methods: {
        getRegisteredComponents() {
            return registeredComponents;
        },
        
        /**
         * Initialisiert Builder.io Content basierend auf der aktuellen Route
         * @param {string} stepName - Name des aktuellen Steps (z.B. "StepOne", "StepTwo")
         * @returns {Promise} - Promise der Builder.io Abfrage
         */
        async initializeBuilderContent(stepName) {
            if (!this.pageSettings) {
                return null;
            }

            try {
                const result = await this.fetchBuilderContent();

                if (this.applyBuilderContentResult(stepName, result)) {
                    return result;
                }

                if (isDevMode) {
                    this.applyDevFallback(stepName);
                } else if (!isPreviewing()) {
                    const canonicalResult =
                        await this.tryRecoverBuilderCanonicalRoute(stepName);

                    if (
                        this.applyBuilderContentResult(
                            stepName,
                            canonicalResult
                        )
                    ) {
                        return canonicalResult;
                    }

                    const retryResult = await this.retryBuilderContent(
                        stepName,
                        "missing_content"
                    );

                    if (this.applyBuilderContentResult(stepName, retryResult)) {
                        return retryResult;
                    }

                    this.handleMissingBuilderContent(stepName);
                }

                return result;
            } catch (error) {
                captureCustomizerException(error, {
                    tags: this.getBuilderEventTags(stepName),
                    context: {
                        urlPath: this.getBuilderUrlPath(),
                        hash: window.location.hash,
                    },
                });

                if (isDevMode) {
                    this.applyDevFallback(stepName);
                    return null;
                }

                const retryResult = await this.retryBuilderContent(
                    stepName,
                    "fetch_error"
                );

                if (this.applyBuilderContentResult(stepName, retryResult)) {
                    return retryResult;
                }

                if (this.tryReloadBuilderContent(stepName, "fetch_error")) {
                    return null;
                }

                this.showBuilderFallback();
                return null;
            }
        },
        
        /**
         * Überprüft ob wir im Builder.io Editor oder Preview sind
         * @returns {boolean}
         */
        isBuilderIoMode() {
            return isEditing() || isPreviewing();
        },

        async fetchBuilderContent() {
            return sanitizeBuilderContent(await fetchOneEntry({
                model: this.model,
                apiKey: this.apiKey,
                options: getBuilderSearchParams(
                    getBuilderMergedSearchParams()
                ),
                userAttributes: {
                    urlPath: this.getBuilderUrlPath(),
                },
            }));
        },

        async fetchBuilderCanonicalRouteEntries(routeVariant) {
            const numericVariantId = getNumericVariantId(routeVariant);
            if (!numericVariantId) return [];

            const entries = [];
            const pageSignatures = new Set();
            let offset = 0;
            let hasMoreEntries = true;

            while (hasMoreEntries) {
                const page = await fetchEntries({
                    model: this.model,
                    apiKey: this.apiKey,
                    query: {
                        data: {
                            variant: numericVariantId,
                        },
                    },
                    fields: "id,published,query,data.url,data.variant",
                    limit: BUILDER_CANONICAL_ROUTE_LIMIT,
                    offset,
                    canTrack: false,
                }) || [];

                entries.push(...page);
                hasMoreEntries =
                    page.length === BUILDER_CANONICAL_ROUTE_LIMIT;

                if (!hasMoreEntries) {
                    return entries;
                }

                const pageSignature = page
                    .map((entry) => entry?.id || "missing-id")
                    .join(":");
                if (pageSignatures.has(pageSignature)) {
                    throw new Error("Builder canonical route pagination repeated");
                }

                pageSignatures.add(pageSignature);
                offset += page.length;
            }

            return entries;
        },

        async tryRecoverBuilderCanonicalRoute(stepName) {
            if (this.isBuilderIoMode()) return null;

            const routeVariant = this.$route?.params?.bookId || "";
            if (!routeVariant) {
                this.builderCanonicalRouteStatus = "invalid_route";
                return null;
            }

            try {
                const entries =
                    await this.fetchBuilderCanonicalRouteEntries(routeVariant);
                const resolution = resolveCanonicalBuilderRoute({
                    routeVariant,
                    stepName,
                    entries,
                });

                this.builderCanonicalRouteStatus = resolution.status;

                if (!resolution.path) return null;

                await this.$router.replace({
                    path: resolution.path,
                    query: this.$route?.query || {},
                });

                incrementCustomizerMetric("customizer.url_recovered", {
                    host: window.location.host,
                    step: stepName,
                    hasHash: Boolean(window.location.hash),
                    route_source: "builder_canonical",
                    recovery_reason: resolution.status,
                });

                const result = await this.fetchBuilderContent();
                this.builderCanonicalRouteStatus = result
                    ? "recovered"
                    : "canonical_fetch_missing";
                return result;
            } catch (error) {
                void error;
                this.builderCanonicalRouteStatus = "recovery_failed";
                incrementCustomizerMetric(
                    "customizer.builder_canonical_route_recovery_failed",
                    {
                        host: window.location.host,
                        step: stepName,
                    }
                );
                return null;
            }
        },

        applyBuilderContentResult(stepName, result) {
            this.content = result;
            this.canShowContent = Boolean(this.content) || isPreviewing();

            if (!this.content?.data) {
                return false;
            }

            this.builderContentMissing = false;
            this.clearBuilderContentReloadAttempt(stepName);
            this.applyContentSettings(stepName, this.content.data);
            trackStartedCustomizer({
                vm: this,
                content: this.content,
                stepName,
            });
            return true;
        },

        getBuilderUrlPath() {
            if (isDevMode) {
                return `/pages/customizer/#${this.$route.path}`;
            }

            const hashParts = window.location.hash.split("?");
            const cleanHash = hashParts[0];
            return window.location.pathname + cleanHash;
        },

        applyContentSettings(stepName, contentData) {
            if (!this.pageSettings) return;

            const settings = buildPageSettingsFromContent(stepName, contentData);
            this.setPageSettings(settings);
        },

        applyDevFallback(stepName) {
            if (!isDevMode || !this.pageSettings) return;
            const settings = buildDevFallbackSettings(stepName);
            this.setPageSettings(settings);
        },

        getBuilderEventTags(stepName) {
            return {
                variant: this.$route?.params?.bookId || this.pageSettings?.variant || "unknown",
                step: stepName,
                urlPath: this.getBuilderUrlPath(),
                hash: window.location.hash || "",
                locale: document.documentElement.lang || "unknown",
                host: window.location.host,
                canonical_route_status:
                    this.builderCanonicalRouteStatus || "not_checked",
            };
        },

        getStepOnePath() {
            const variant =
                this.$route?.params?.bookId ||
                this.pageSettings?.variant ||
                new URLSearchParams(window.location.search).get("variant");

            return variant ? `/${variant}/step1` : "";
        },

        getBuilderContentReloadKey(stepName) {
            return [
                BUILDER_CONTENT_RELOAD_KEY_PREFIX,
                window.location.host,
                this.getBuilderUrlPath(),
                stepName,
            ].join(":");
        },

        clearBuilderContentReloadAttempt(stepName) {
            safeRemoveItem(this.getBuilderContentReloadKey(stepName));
        },

        async retryBuilderContent(stepName, reason) {
            if (this.isBuilderIoMode()) {
                return null;
            }

            incrementCustomizerMetric("customizer.builder_content_retry", {
                host: window.location.host,
                step: stepName,
                reason,
                product: this.pageSettings?.product || "unknown",
            });

            await wait(BUILDER_CONTENT_RETRY_DELAY_MS);

            try {
                return await this.fetchBuilderContent();
            } catch (error) {
                captureCustomizerException(error, {
                    tags: {
                        ...this.getBuilderEventTags(stepName),
                        reason,
                        retry: "true",
                    },
                    context: {
                        currentRoute: this.$route?.path,
                        urlPath: this.getBuilderUrlPath(),
                        search: window.location.search,
                    },
                });

                return null;
            }
        },

        tryReloadBuilderContent(stepName, reason) {
            if (this.isBuilderIoMode()) {
                return false;
            }

            const reloadKey = this.getBuilderContentReloadKey(stepName);
            const lastReloadAt = Number(safeGetItem(reloadKey, "0"));
            const now = Date.now();

            if (
                lastReloadAt &&
                now - lastReloadAt < BUILDER_CONTENT_RELOAD_WINDOW_MS
            ) {
                return false;
            }

            if (!safeSetItem(reloadKey, String(now))) {
                return false;
            }

            incrementCustomizerMetric("customizer.builder_content_reload", {
                host: window.location.host,
                step: stepName,
                reason,
                product: this.pageSettings?.product || "unknown",
            });

            window.setTimeout(() => {
                window.location.reload();
            }, 150);

            return true;
        },

        handleMissingBuilderContent(stepName) {
            captureCustomizerMessage("builder_content_missing", {
                level: "error",
                tags: this.getBuilderEventTags(stepName),
                context: {
                    currentRoute: this.$route?.path,
                    urlPath: this.getBuilderUrlPath(),
                    search: window.location.search,
                },
            });

            if (["StepTwo", "StepThree"].includes(stepName)) {
                const redirectPath = this.getStepOnePath();
                const redirectKey = `${BUILDER_STEP_REDIRECT_KEY_PREFIX}:${redirectPath}`;

                if (redirectPath && safeGetItem(redirectKey, "") !== "1") {
                    safeSetItem(redirectKey, "1");
                    this.$router.replace(redirectPath);
                    return;
                }
            }

            if (this.tryReloadBuilderContent(stepName, "missing_content")) {
                return;
            }

            this.showBuilderFallback();
        },

        showBuilderFallback() {
            this.content = null;
            this.canShowContent = false;
            this.builderContentMissing = true;
        },

        setPageSettings(newSettings) {
            Object.assign(this.pageSettings, newSettings);
            EventBus.emit("pageSettingsUpdated", newSettings);
        },
    }
};
